import { Button, Icon, Tag, TagLabel } from '@deque/cauldron-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { type Test } from '../../common/api-client';
import {
  getFilterLabels,
  getFilterValues,
  type UpdateFilter
} from '../../common/hooks/useIssuesFilter';
import ElementMarkup from './ElementMarkup';
import styles from './TestSummary.css';
import type { Filters } from '@deque/axe-devtools-issues-utils';

interface TestSummaryProps {
  test: Test;
  filters: Filters;
  updateFilter: UpdateFilter;
}

const TestSummary: React.FC<TestSummaryProps> = ({
  test,
  filters,
  updateFilter
}) => {
  const { t } = useTranslation();

  const filterLabels = getFilterLabels(t);
  const rulesetFilter =
    filters.configAccessibilityStandard || filters.configWCAGLevel;
  const rulesetName =
    filterLabels.configAccessibilityStandard.values.get(rulesetFilter);

  const bestPracticesState = getFilterValues('configBestPractices', t).get(
    filters.configBestPractices
  );

  const needsReviewState = getFilterValues('configNeedsReview', t).get(
    filters.configNeedsReview
  );

  const toggleBestPractices = () => {
    updateFilter(
      'configBestPractices',
      filters.configBestPractices === 'enabled' ? 'disabled' : 'enabled'
    );
  };

  return (
    <div className={styles.wrap}>
      <dl>
        <div>
          <dt>{t('URL:')}</dt>
          <dd>
            <a href={test.url} target="_blank" rel="noopener noreferrer">
              {test.url}
              <Icon type="external-link" label={t('Open in new tab')} />
            </a>
          </dd>
        </div>
        {test.metadata?.scope && (
          <div className={styles.element}>
            <dt>{t('Element:')}</dt>
            <dd>
              <ElementMarkup element={test.metadata?.scope} />
            </dd>
          </div>
        )}
      </dl>
      <div className={styles.filters}>
        <Tag data-testid="accessibility-standard-filter">{rulesetName}</Tag>
        <div>
          <Button variant="tag" onClick={toggleBestPractices}>
            <TagLabel>{t('Best Practices:')}</TagLabel>
            <em>{bestPracticesState}</em>
          </Button>
          <Tag data-testid="needs-review-filter">
            <TagLabel>{t('Needs Review:')}</TagLabel>
            <em>{needsReviewState}</em>
          </Tag>
        </div>
      </div>
    </div>
  );
};

export default TestSummary;
